import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useTimeJumpModal } from '@/hooks/useTimeJumpModal';
import { TimeProgressionService } from '@/services/timeProgressionService';
import { useTimeProgressionRealtime } from '@/hooks/useRealtimeManager';
import { usePlayerAuth } from '@/hooks/usePlayerAuth';
import { usePlayerCoreStore } from '@/stores/usePlayerCoreStore';
import { isFreeWorldRoute, isModuleRoute } from '@/hooks/useModuleBootstrap';
// DEPRECATED: Market tick system removed - prices now come from market_prices table
// import { addTicksForPeriodJump } from '@/services/marketTickService';
import { devLog, devWarn } from '@/lib/devPerfLog';
import type { TimePeriod, PlayerFinancialStatus } from '@/types/timeProgression';

/**
 * useTimeProgression - FreeWorld-only time progression hook
 * 
 * CRITICAL: This hook should ONLY be used on FreeWorld routes.
 * On module routes, it will skip all FreeWorld-specific logic (checkTaskCompletion, etc.)
 */
export function useTimeProgression() {
  // CRITICAL: Check route type early - skip most logic on module routes
  const onFreeWorldRoute = isFreeWorldRoute();
  const onModuleRoute = isModuleRoute();

  // Get core data from unified store
  const {
    playerId,
    sessionToken,
    currentPeriod: storePeriod,
    financialStatus: storeFinancialStatus,
    playerAge: storePlayerAge,
    isReady,
    isLoading: storeLoading,
    setCurrentPeriod,
    setFinancialStatus,
    setPlayerAge,
    refresh: refreshCore
  } = usePlayerCoreStore();
  
  // Local state for additional UI concerns
  const [isAdvancingTime, setIsAdvancingTime] = useState(false);
  const [isGameCompleted, setIsGameCompleted] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  const { playerSession } = usePlayerAuth();
  const { toast } = useToast();
  
  const {
    showTimeJumpModal,
    timeJumpSummary,
    timeJumpDuration,
    nextPeriod,
    restartCount,
    checkAndShowTimeJump,
    acknowledgeTimeJump,
    handleRestart,
    resetShownSummary
  } = useTimeJumpModal();

  // Subscribe to time progression updates using centralized manager
  // ONLY on FreeWorld routes
  const handleTimeProgressionUpdate = useCallback(() => {
    if (isReady && onFreeWorldRoute) {
      refreshCore();
    }
  }, [isReady, refreshCore, onFreeWorldRoute]);

  useTimeProgressionRealtime(handleTimeProgressionUpdate, !!playerId && onFreeWorldRoute);

  // Check for game completion when period changes
  // CRITICAL: Only run on FreeWorld routes to avoid loading FreeWorld data on /module/* routes
  useEffect(() => {
    // HARD GATE: Only check task completion on FreeWorld routes
    // This is the critical guard that prevents FreeWorld data loading on /module/*
    if (onModuleRoute) {
      if (import.meta.env.DEV) {
        console.debug('[useTimeProgression] SKIPPED checkTaskCompletion on module route');
      }
      return;
    }
    
    if (!onFreeWorldRoute) {
      // Not on FreeWorld route, skip all FreeWorld logic
      return;
    }
    
    if (!isReady || !storePeriod || !storeFinancialStatus || !playerId || !sessionToken) {
      return;
    }
    
    const checkCompletion = async () => {
      try {
        resetShownSummary();
        
        const allTasksCompleted = await TimeProgressionService.checkTaskCompletion(
          playerId,
          storePeriod.id,
          sessionToken
        );

        if (allTasksCompleted) {
          const next = await TimeProgressionService.getNextTimePeriod(storePeriod.id);
          
          if (next) {
            checkAndShowTimeJump(storeFinancialStatus, next);
            setIsGameCompleted(false);
          } else {
            setIsGameCompleted(true);
          }
        }
      } catch (err) {
        devWarn('TimeProgress', 'Error checking completion', err);
      }
    };

    checkCompletion();
  }, [isReady, storePeriod?.id, storeFinancialStatus, playerId, sessionToken, onModuleRoute, onFreeWorldRoute]);

  const updateMoney = async (amount: number, type: 'earned' | 'spent') => {
    if (!storeFinancialStatus || !playerId || !sessionToken) {
      devWarn('TimeProgress', 'updateMoney blocked: missing required state');
      return false;
    }

    try {
      const newCurrentMoney = storeFinancialStatus.current_money + amount;
      
      if (newCurrentMoney < 0 && type === 'spent') {
        toast({
          title: "Insufficient Funds",
          description: "You don't have enough money for this action!",
          variant: "destructive"
        });
        return false;
      }

      const updates = {
        current_money: newCurrentMoney,
        ...(type === 'earned' 
          ? { total_earned: storeFinancialStatus.total_earned + Math.abs(amount) }
          : { total_spent: storeFinancialStatus.total_spent + Math.abs(amount) }
        )
      };

      const updatedStatus = await TimeProgressionService.updateFinancialStatus(
        playerId, 
        updates, 
        sessionToken
      );
      
      if (updatedStatus) {
        setFinancialStatus(updatedStatus);
        
        // Dispatch event to notify all listeners (HUD, balance hook, etc.)
        window.dispatchEvent(new CustomEvent('moneyChanged', {
          detail: {
            currentMoney: updatedStatus.current_money,
            newTotalEarned: updatedStatus.total_earned,
            reason: type
          }
        }));
        
        return true;
      } else {
        toast({
          title: "Virhe",
          description: "Rahatietojen päivitys epäonnistui",
          variant: "destructive"
        });
        return false;
      }

    } catch (err) {
      devLog('TimeProgress', 'Error updating money', err);
      toast({
        title: "Error",
        description: "Failed to update money",
        variant: "destructive"
      });
      return false;
    }
  };

  const earnMoney = async (amount: number) => {
    return updateMoney(amount, 'earned');
  };
  
  const spendMoney = async (amount: number) => {
    return updateMoney(-amount, 'spent');
  };

  const handleTimeJumpAcknowledgment = async (onTimeAdvance?: (duration: { value: number; unit: string }) => void) => {
    // Time jump acknowledgment - market ticks are handled in advanceTimePeriod
    acknowledgeTimeJump(onTimeAdvance);
  };

  const advanceTimePeriod = async (options: {
    expectedCurrentPeriodId?: string | null;
  } = {}) => {
    if (!playerId || !sessionToken) {
      devWarn('TimeProgress', 'Cannot advance time - missing player/session', {
        hasPlayerId: !!playerId,
        hasSessionToken: !!sessionToken
      });
      throw new Error('Istunto puuttuu. Yritä päivittää sivu.');
    }
    
    setIsAdvancingTime(true);
    try {
      const result = await TimeProgressionService.advanceToNextPeriod({
        playerId,
        sessionToken,
        expectedCurrentPeriodId: options.expectedCurrentPeriodId
      });
      
      // Update store immediately for responsive UI
      if (result?.newPeriod) {
        setCurrentPeriod(result.newPeriod);
      }
      if (result?.ageAdvanced && storePlayerAge !== null) {
        setPlayerAge(storePlayerAge + result.ageAdvanced);
        
        // DEPRECATED: Market tick system removed
        // Prices now come from market_prices table (updated by cron edge function)
        if (import.meta.env.DEV) {
          console.log('[TimeProgression] Time period advanced:', {
            yearsAdvanced: result.ageAdvanced,
            note: 'Market tick system disabled - using market_prices'
          });
        }
      }
      
      // Refresh to ensure consistency
      await refreshCore();
      
      return result;
    } catch (err) {
      devWarn('TimeProgress', 'Error advancing time', err);
      throw err;
    } finally {
      setIsAdvancingTime(false);
    }
  };

  const loadTimeProgression = async () => {
    await refreshCore();
  };

  return {
    currentPeriod: storePeriod,
    nextPeriod,
    financialStatus: storeFinancialStatus,
    playerAge: storePlayerAge,
    showTimeJumpModal,
    timeJumpSummary,
    timeJumpDuration,
    restartCount,
    isInitialLoading: !isReady || storeLoading,
    isAdvancingTime,
    error,
    earnMoney,
    spendMoney,
    acknowledgeTimeJump: handleTimeJumpAcknowledgment,
    handleRestart,
    loadTimeProgression,
    checkAndShowTimeJump,
    advanceTimePeriod,
    isGameCompleted
  };
}
